import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable, tap } from 'rxjs';
import { LoginRequest, LoginRequestOTP } from "~features/authentication/types/login-request.type";
import { LoginResponseData, LoginResponseOTPData } from "~shared/types/login-response.type";
import { ApiResponseOrError } from "~shared/types/api-response.types";
import { getEndpoints } from "~core/constants/endpoints.constants";
import { APP_TYPE } from "~core/constants/title.constants";
import { AuthService } from "~core/services/auth.service";
import { SessionService } from "~core/services/session.service";

@Injectable({
    providedIn: 'root'
})
export class LoginService {
    private http = inject(HttpClient);
    private authService = inject(AuthService);
    private sessionService = inject(SessionService);
    private readonly endpoints = getEndpoints();

    /**
     * Chiama l'endpoint /login
     */
    login(loginRequest: LoginRequest): Observable<LoginResponseData> {
        const payload = {
            email: loginRequest.email.toLowerCase(),
            password: loginRequest.password,
            type: APP_TYPE,
            application: "Selforder"
        };
        const _loginurl = this.endpoints.auth.v3.login;
        return this.http
            .post<ApiResponseOrError<LoginResponseData>>(_loginurl, payload)
            .pipe(
                map((data) => this.handleLoginResponse(data)),
                tap((data) => {
                    if (data && !data.mfa_required) {
                        this.authService.setAuthorized(payload.email);
                    }
                })
            );
    }

    /**
     * Chiama l'endpoint /logintoken
     */
    logintoken(token: string): Observable<boolean> {
        const payload = {
            token: token,
            application: "Selforder"
        };
        const _loginurl = this.endpoints.auth.v3.logintoken;
        return this.http
            .post<ApiResponseOrError<LoginResponseData>>(_loginurl, payload)
            .pipe(
                map((response) => {
                   // console.log('Response check qrtoken:', response);
                    if ('ok' in response && response.ok) {
                        // v3 restituisce data.email, i backend non ancora aggiornati data.user.email
                        const data = response.data as LoginResponseData & { user?: { email?: string } };
                        const email = (data?.email ?? data?.user?.email)?.trim().toLowerCase();
                        if (!email) {
                            throw new Error('Email non presente nella risposta');
                        }
                        // sessione precedente di un altro utente: va pulita prima di impostare la nuova
                        const previousEmail = this.authService.authEmail();
                        if (previousEmail && previousEmail !== email) {
                            this.sessionService.logout();
                        }
                        console.log("autorizzato a entrare")
                        this.authService.setAuthorized(email);
                        return true;
                    }
                    if ('error' in response) {
                        throw new Error(response.error?.message ?? 'Token non valido');
                    }
                    throw new Error('Token non valido');
                })
            );
    }

    /**
     * Chiama l'endpoint /mfacheck
     */
    checkMfa(mfarequest: LoginRequestOTP): Observable<LoginResponseOTPData> {
        const payload = {
            email: mfarequest.email,
            mfa_code: mfarequest.mfa_code,
            mfa_flag: mfarequest.mfa_flag
        };
        const _mfaurl = this.endpoints.auth.v3.mfacheck;
        return this.http
            .post<ApiResponseOrError<LoginResponseOTPData>>(_mfaurl, payload)
            .pipe(
                map((data) => this.handleOtpResponse(data)),
                tap((data) => {
                    if (data && data.mfa_check) {
                        this.authService.setAuthorized(payload.email);
                    }
                })
            );
    }

    private handleLoginResponse(data: ApiResponseOrError<LoginResponseData>): LoginResponseData {
        if ('ok' in data) {
            return data.data;
        }
        throw new Error(data.error?.message ?? 'Errore generico');
    }

    private handleOtpResponse(data: ApiResponseOrError<LoginResponseOTPData>): LoginResponseOTPData {
        if ('ok' in data) {
            return data.data;
        }
        throw new Error(data.error?.message ?? 'Errore generico');
    }
}
